import {
  getDeleteApiInstitutionIdMutationOptions,
  type InstitutionViewDTO,
} from '@smartsash/api-client';
import { useEntityDelete } from '@smartsash/server-ops';
import { Icon, openModal } from '@smartsash/ui';
import { useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { DEVICES_INSTITUTION_FIELD, devicesFilteredBy } from '@/ui/pages/Devices/devicesModel';
import { groupsFilteredByInstitution } from '@/ui/pages/Groups/groupsModel';
import { laboratoriesFilteredByInstitution } from '@/ui/pages/Laboratories/laboratoriesModel';
import { usersFilteredByInstitution } from '@/ui/pages/Users/usersModel';
import { RowActions as Shared, type RowActionEntry } from '@/ui/shared/RowActions';

function RowActions({ row }: { row: InstitutionViewDTO }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = row.id ?? '';
  // HS-847: delete via server-ops so the institution list query is invalidated
  // (list refreshes in place) and a toast is emitted through the shared sink.
  const del = useEntityDelete({
    entity: 'institution',
    mutationOptions: getDeleteApiInstitutionIdMutationOptions,
    // HS-1141: the deleted record's own reads are dropped, not refetched into a 404.
    deletesRecord: true,
  });
  // HS-1177: the imperative modal replaces a `useState` pair plus a conditionally rendered
  // dialog. It resolves with the pressed action's `value`, or `null` when dismissed.
  const handleDelete = useCallback(async () => {
    const { action } = await openModal({
      title: t('institutes.deleteTitle', { name: row.name }),
      description: t('institutes.deleteDescription'),
      variant: 'error',
      icon: <Icon name="trash-01" />,
      actions: [
        { label: t('common.cancel'), value: 'cancel' },
        { label: t('common.delete'), value: 'confirm', destructive: true },
      ],
    }).closed;

    if (action === 'confirm') del.mutate({ id });
  }, [del, id, row.name, t]);

  const items: RowActionEntry[] = [
    {
      label: t('common.edit'),
      permission: 'institution.edit',
      icon: 'edit-01',
      onClick: () => {
        void navigate({ to: '/institutions/$id/edit', params: { id } });
      },
    },
    null,
    // HS-1376: first among the "View X" entries — mirrors the ordering the InstitutionDetails
    // header/kebab restructuring already established for this same relation.
    {
      label: t('institutes.actions.viewGroups'),
      icon: 'dataflow-03',
      onClick: () => {
        void navigate({ to: '/groups', search: groupsFilteredByInstitution(id) });
      },
    },
    {
      label: t('institutes.actions.viewLaboratories'),
      icon: 'lab',
      onClick: () => {
        void navigate({ to: '/laboratories', search: laboratoriesFilteredByInstitution(id) });
      },
    },
    {
      label: t('institutes.actions.viewDevices'),
      icon: 'tv-01',
      onClick: () => {
        void navigate({
          to: '/devices',
          search: devicesFilteredBy(DEVICES_INSTITUTION_FIELD, id),
        });
      },
    },
    {
      label: t('institutes.actions.viewUsers'),
      icon: 'users-02',
      onClick: () => {
        void navigate({ to: '/users', search: usersFilteredByInstitution(id) });
      },
    },
    null,
    {
      label: t('common.delete'),
      permission: 'institution.delete',
      icon: 'trash-01',
      onClick: () => {
        void handleDelete();
      },
    },
  ];

  return <Shared items={items} />;
}

export default RowActions;
